export type CourseModule = {
  title: string;
  description: string;
  topics: string[];
};

export type CourseDetailInfo = {
  tagline?: string;
  overview?: string;
  overviewSubtext?: string;
  metaTitle?: string;
  metaDescription?: string;
  imageAlt?: string;
  highlights?: string[];
  outcomes?: string[];
  modules?: CourseModule[];
  projects?: string[];
  prerequisites?: string[];
  startDates?: string[];
  tools?: string[];
};

export type CourseData = {
  title: string;
  description: string;
  slug: string;
  image: string;
  category: string;
  featured: boolean;
  duration: string;
  detail?: CourseDetailInfo;
};

export const categories = [
  "All Courses",
  "Web Development",
  "Programming"
] as const;

export const coursesData: CourseData[] = [
  {
    "title": "30 days Web Design & Development",
    "description": "Elevate your skills with our 30-day transformative course. Whether you're starting fresh or refining your expertise, you'll master dynamic website creation, craft compelling ad designs, and harness the power of AI to lead in digital marketing. This program is tailored for individuals with zero technical or coding knowledge.",
    "slug": "web-design-development-nocode",
    "image": "https://images.unsplash.com/photo-1498050108023-c5249f4df085",
    "category": "Web Development",
    "featured": true,
    "duration": "30 Days",
    "detail": {
      "metaTitle": "30 Days Web Design & Development Course | CODO Academy",
      "metaDescription": "Join the 30 Days Web Design & Development Course at Codo Academy and learn to build modern websites using HTML, CSS, and practical design skills.",
      "imageAlt": "Software Development Course in Kerala",
      "overviewSubtext": "This software development course is ideal for beginners looking for a practical website development course in Kerala with real-world outcomes.",
      "tagline": "Launch a professional website, master online ads, and achieve financial independence as a freelancer.",
      "highlights": [
        "Master dynamic, static, and e-commerce websites (WordPress, Shopify, Wix).",
        "Design compelling ad content using Canva Premium.",
        "Achieve financial independence with potential earnings of ₹50,000+ per month.",
        "Receive FREE lifetime mentorship and a 1-year free hosting/domain plan."
      ],
      "modules": [
        {
          "title": "CMS & Website Foundations",
          "description": "Master the fundamentals of the internet, including domain names and web hosting (Hostinger). Learn to navigate and manage a Content Management System (CMS), focusing on WordPress essentials.",
          "topics": [
            "Introduction to Websites & Hosting Basics",
            "Getting Started with WordPress & Dashboard Settings",
            "Securing and optimizing your WordPress setup"
          ]
        },
        {
          "title": "Advanced Page Building & E-commerce",
          "description": "Go hands-on with page builders like Elementor and learn to use premium resources like Envato template kits. Master e-commerce platforms to build high-converting online stores.",
          "topics": [
            "Mastering Elementor and Plugin Integrations",
            "Building a custom dummy website & Mega menu setup",
            "Creating professional e-commerce stores with Shopify, WooCommerce, and WIX"
          ]
        },
        {
          "title": "Digital Marketing & SEO Mastery",
          "description": "Unlock the power of online advertising. Learn to design visuals and run effective, targeted campaigns on the world's largest advertising platforms.",
          "topics": [
            "Designing ad visuals with Canva Premium",
            "Advanced SEO, Analytics, and Social Media Growth Hacking"
          ]
        },
        {
          "title": "Professional Portfolio & Career Launch",
          "description": "Finalize your professional portfolio and gain the essential business skills for freelancing. Learn how to quote, invoice, and secure high-paying clients.",
          "topics": [
            "Portfolio creation, debugging, and optimization",
            "Crafting standout proposals and quotation preparation",
            "Freelancing strategies and building mentor networks"
          ]
        }
      ],
      "projects": [
        "Create a professional, live portfolio website (ready for clients).",
        "Build a fully functional e-commerce storefront (Shopify/WooCommerce).",
        "Launch an effective Google Ads campaign with ROI focus.",
        "Prepare a comprehensive quotation and invoice for a client project."
      ],
      "startDates": [
        "Next Chapter Soon!"
      ],
      "tools": [
        "WordPress",
        "Shopify",
        "WIX",
        "WooCommerce",
        "Hostinger (1-Year Free)",
        "Elementor",
        "Canva Premium",
        "Google Ads",
        "Meta Ads"
      ]
    }
  },
  {
    "title": "3 Month Vibe Coding Bootcamp: Full-Stack JavaScript",
    "description": "The 12-week intensive bootcamp designed for the modern web. Master the entire JavaScript ecosystem, from building aesthetic, blazing-fast interfaces with Next.js and Tailwind CSS, to architecting scalable backend APIs with Node.js and MongoDB. Go from zero to deployment with an industry-ready portfolio.",
    "slug": "3-month-vibe-coding-course",
    "image": "https://images.unsplash.com/photo-1542831371-29b0f74f9713",
    "category": "Programming",
    "featured": true,
    "duration": "12 Weeks (3 Months)",
    "detail": {
      "metaTitle": "3 Month Vibe Coding Bootcamp: Full-Stack JavaScript | CODO Academy",
      "metaDescription": "Join the 3 Month Vibe Coding Bootcamp at Codo Academy and master Full-Stack JavaScript, building real-world web applications with hands-on training.",
      "imageAlt": "Full Stack JavaScript Course in Kerala",
      "overviewSubtext": "This full stack JavaScript course is designed for learners seeking a practical full stack developer course in Kerala, with hands-on MERN stack training and real deployment experience.",
      "tagline": "Build beautiful, scalable, and production-ready applications with the latest tools.",
      "highlights": [
        "Master the MERN stack (MongoDB, Express, React, Node.js) and Next.js framework.",
        "Build highly responsive and custom-styled applications using Tailwind CSS.",
        "Develop and secure robust RESTful and GraphQL APIs.",
        "Integrate modern authentication (Auth0/NextAuth) and database technologies (MongoDB/PostgreSQL).",
        "Implement professional CI/CD pipelines for seamless deployment on Vercel/Netlify."
      ],
      "modules": [
        {
          "title": "Module 1: JavaScript & Frontend Fundamentals",
          "description": "Establish a rock-solid foundation in modern web standards. Learn the latest ES2023 features, TypeScript for type safety, and how to style applications professionally with the utility-first CSS framework.",
          "topics": [
            "HTML5 & Semantic Markup",
            "Advanced CSS (Flexbox, Grid, Responsiveness)",
            "Modern JavaScript (ES6+ features, Async/Await)",
            "Introduction to TypeScript",
            "Utility-First Styling with Tailwind CSS"
          ]
        },
        {
          "title": "Module 2: React & Next.js Interface Engineering",
          "description": "Dive deep into component-based architecture using React and the server-first framework, Next.js. Master state management, routing, data fetching, and performance optimization techniques for a fast user experience.",
          "topics": [
            "Component-Based Architecture (Hooks, Context)",
            "State Management (Redux/Zustand)",
            "React Routing & Next.js File-System Routing",
            "Server-Side Rendering (SSR) & Static Site Generation (SSG)",
            "Frontend Testing with Jest/React Testing Library"
          ]
        },
        {
          "title": "Module 3: Backend Development with Node.js & APIs",
          "description": "Learn how to build, test, and secure the server-side of your application. You will architect robust APIs using Express.js and integrate flexible NoSQL databases like MongoDB.",
          "topics": [
            "Node.js and Express.js Fundamentals",
            "RESTful API Design Principles",
            "Database Integration (MongoDB/Mongoose)",
            "Authentication and Authorization (JWTs, Passport.js)",
            "API Security & Error Handling"
          ]
        },
        {
          "title": "Module 4: Full-Stack Integration & Deployment (Capstone)",
          "description": "Combine your frontend and backend skills into a complete, deployable product. Focus on project management, quality assurance, and automated deployment processes required by top companies.",
          "topics": [
            "Project Planning and Architectural Decisions",
            "Full-Stack Integration and Data Flow",
            "Version Control with Git and GitHub",
            "CI/CD Pipelines and Deployment (Vercel, Netlify, Render)",
            "Observability, Monitoring, and Analytics"
          ]
        }
      ],
      "projects": [
        "A feature-rich Social Media Clone (Full MERN Stack)",
        "A modern, custom E-commerce Site with Stripe payment integration (Next.js/PostgreSQL)",
        "A Real-Time Collaboration Tool using WebSockets (Node.js/Socket.io)"
      ],
      "startDates": [
        "Next Chapter Soon!"
      ],
      "tools": [
        "React",
        "Next.js",
        "TypeScript",
        "Tailwind CSS",
        "Node.js",
        "Express.js",
        "MongoDB",
        "Git/GitHub",
        "Vercel/Netlify",
        "Postman"
      ]
    }
  },
  {
    "title": "3 Month Python Full-Stack Development Bootcamp",
    "description": "The 12-week intensive Python bootcamp. Master the entire modern Python stack, building powerful web applications using Django/Flask for the backend and pairing it with a professional frontend (React/HTMX). Learn database management, API design, deployment, and testing.",
    "slug": "3-month-python-full-stack",
    "image": "https://images.unsplash.com/photo-1526379095098-d400fd0bf935",
    "category": "Programming",
    "featured": true,
    "duration": "12 Weeks (3 Months)",
    "detail": {
      "metaTitle": "3 Month Python Full-Stack Development Bootcamp | CODO Academy",
      "metaDescription": "Join the 3 Month Python Full-Stack Development Bootcamp at Codo Academy and learn to build modern web applications using Python, databases, and real projects.",
      "imageAlt": "Job-Ready Python Full Stack Developer Course in Kerala",
      "tagline": "Build scalable and secure web applications using the robust Python ecosystem.",
      "highlights": [
        "Achieve mastery in Python programming and object-oriented principles.",
        "Develop robust backend applications using Django and Django REST Framework.",
        "Design and manage relational databases with PostgreSQL.",
        "Integrate front-end interfaces (React or modern templating) via APIs.",
        "Implement professional security practices, testing, and cloud deployment on platforms like AWS/Heroku."
      ],
      "modules": [
        {
          "title": "Module 1: Python Programming & Fundamentals",
          "description": "Establish a strong foundation in Python. Cover core syntax, data structures, and Object-Oriented Programming (OOP) concepts necessary for full-stack application development.",
          "topics": [
            "Python syntax, data types, and control flow",
            "Advanced functions and modules",
            "Object-Oriented Programming (OOP) and best practices",
            "Virtual environments and package management (pip)"
          ]
        },
        {
          "title": "Module 2: Django Backend & Database Mastery",
          "description": "Dive into the Django framework to build your backend. Learn the MVC architecture, work with models, views, and templates, and connect to a powerful relational database.",
          "topics": [
            "Django architecture (MVT pattern)",
            "Defining models and migrations (ORM)",
            "URL routing, views, and template rendering (Jinja)",
            "Database management with PostgreSQL/SQLite",
            "User authentication and authorization"
          ]
        },
        {
          "title": "Module 3: REST APIs & Frontend Integration",
          "description": "Learn to transform your backend into a data service using Django REST Framework (DRF). Build a modern frontend (using React) to consume these APIs, establishing a true full-stack architecture.",
          "topics": [
            "Building RESTful APIs with Django REST Framework (DRF)",
            "Serializer creation and data validation",
            "Introduction to modern JavaScript/React fundamentals",
            "Data fetching and state management on the frontend",
            "Integrating React with Python APIs"
          ]
        },
        {
          "title": "Module 4: Deployment, Testing, and DevOps",
          "description": "Complete the developer lifecycle by mastering testing, continuous integration, and deploying your application to a production environment.",
          "topics": [
            "Unit and integration testing with pytest",
            "Securing APIs and handling environment variables",
            "Cloud deployment (Heroku, AWS/Digital Ocean)",
            "Containerization basics with Docker (optional/advanced)",
            "CI/CD pipeline setup (e.g., GitHub Actions)"
          ]
        }
      ],
      "projects": [
        "A Multi-User Blog/CMS Platform (Full Django Monolith)",
        "A Task Management App with a REST API and React Frontend (Django + React)",
        "A Simple E-commerce Platform with Payment Integration (Django/Stripe)"
      ],
      "startDates": [
        "Next Chapter Soon!"
      ],
      "tools": [
        "Python 3",
        "Django",
        "Django REST Framework (DRF)",
        "PostgreSQL",
        "React (for API integration)",
        "HTML/CSS/Tailwind (for styling)",
        "Git/GitHub",
        "Heroku/AWS",
        "Pytest"
      ]
    }
  }

];
